import type { PropsWithChildren } from "react";
import React, { useCallback } from "react";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import type { ReportInfo, ReportUsage } from "../../../../../../api/types/svTypes.ts";
import DeleteReport from "../../reports/components/DeleteReport.tsx";
import DuplicateReport from "../../reports/components/DuplicateReport";
import MoveReportsToGroup from "../../reports/components/MoveReportsToGroup.tsx";
import GenerateApiReport from "../../reports/components/GenerateApiReport";
import ShareReportWrapper from "../../reports/components/ShareReportWrapper";
import ShareReportsBulkWrapper from "../../reports/components/ShareReportsBulkWrapper.tsx";
import CopyToDialog from "../../reports/copyto/CopyToDialog";
import { useReportsContext } from "../reports/ReportsContext";
import { ReportsManagerContextProvider } from "./ReportsManagerContextProvider";

interface Props {
  reports: ReportInfo[];
  reportUsage: ReportUsage;
  openInNewTab?: boolean;
  onCopied?: () => void;
  onDeleted?: () => void;
}
export default function ReportsManager({
  reports,
  children,
  onCopied,
  onDeleted,
  reportUsage,
  openInNewTab,
}: PropsWithChildren<Props>) {
  const { sendNotification } = useNotificationContext();
  const { actions } = useReportsContext();

  const [openCopyToDialog, setOpenCopyToDialog] = React.useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = React.useState(false);
  const [openMoveDialog, setOpenMoveDialog] = React.useState(false);
  const [openDuplicateDialog, setOpenDuplicateDialog] = React.useState(false);
  const [openGenerateApiReport, setOpenGenerateApiReport] = React.useState(false);
  const [openShareDialog, setOpenShareDialog] = React.useState(false);
  const [openBulkShareDialog, setOpenBulkShareDialog] = React.useState(false);

  const [reportsToCopy, setReportsToCopy] = React.useState<ReportInfo[]>([]);
  const [reportsToDelete, setReportsToDelete] = React.useState<ReportInfo[]>([]);
  const [reportsToMove, setReportsToMove] = React.useState<ReportInfo[]>([]);
  const [onReportsMoved, setOnReportsMoved] = React.useState<(() => void) | undefined>();
  const [reportToDuplicate, setReportToDuplicate] = React.useState<ReportInfo>();
  const [reportToGenerateApiReport, setReportToGenerateApiReport] = React.useState<ReportInfo>();
  const [reportToShare, setReportToShare] = React.useState<ReportInfo>();
  const [reportsToShare, setReportsToShare] = React.useState<ReportInfo[]>([]);
  const [onReportsShared, setOnReportsShared] = React.useState<(() => void) | undefined>();

  const copyReports = useCallback(
    (ids: string[]) => {
      const toCopy = reports.filter((r) => ids.includes(r.reportId));
      setReportsToCopy(toCopy);
      setOpenCopyToDialog(true);
    },
    [reports]
  );

  const deleteReports = useCallback(
    (ids: string[]) => {
      const toDelete = reports.filter((r) => ids.includes(r.reportId));
      setReportsToDelete(toDelete);
      setOpenDeleteDialog(true);
    },
    [reports]
  );

  const moveReports = useCallback(
    (ids: string[], onMoved?: () => void) => {
      const toMove = reports.filter((r) => ids.includes(r.reportId));
      setReportsToMove(toMove);
      setOnReportsMoved(() => onMoved);
      setOpenMoveDialog(true);
    },
    [reports]
  );

  const duplicateReport = useCallback((report: ReportInfo) => {
    setReportToDuplicate(report);
    setOpenDuplicateDialog(true);
  }, []);

  const handleGenerateApiReport = useCallback((report: ReportInfo) => {
    setReportToGenerateApiReport(report);
    setOpenGenerateApiReport(true);
  }, []);

  const shareReport = useCallback((report: ReportInfo) => {
    setReportToShare(report);
    setOpenShareDialog(true);
  }, []);

  const shareReports = useCallback(
    (ids: string[], onShared?: () => void) => {
      setReportsToShare(reports.filter((r) => ids.includes(r.reportId)));
      setOnReportsShared(() => onShared);
      setOpenBulkShareDialog(true);
    },
    [reports]
  );

  return (
    <ReportsManagerContextProvider
      copyReports={copyReports}
      deleteReports={deleteReports}
      moveReports={moveReports}
      duplicateReport={duplicateReport}
      shareReport={shareReport}
      shareReports={shareReports}
      generateApiReport={handleGenerateApiReport}
    >
      {children}
      {openCopyToDialog && reportsToCopy.length > 0 && (
        <CopyToDialog
          reports={reportsToCopy}
          onClose={(success) => {
            setOpenCopyToDialog(false);
            setReportsToCopy([]);
            if (success === true) {
              onCopied?.();
            }
          }}
          reportUsage={reportUsage}
        />
      )}
      {openDeleteDialog && reportsToDelete.length > 0 && (
        <DeleteReport
          reports={reportsToDelete}
          doDelete={actions.deleteReports}
          onDeleted={() => {
            sendNotification(
              reportsToDelete.length > 1
                ? "The reports were successfully deleted."
                : "The report was successfully deleted."
            );
            onDeleted?.();
          }}
          onClose={() => {
            setOpenDeleteDialog(false);
            setReportsToDelete([]);
          }}
        />
      )}
      {openMoveDialog && reportsToMove.length > 0 && (
        <MoveReportsToGroup
          reports={reportsToMove}
          doMove={actions.changeReportsGroup}
          onMoved={() => {
            sendNotification(
              reportsToMove.length > 1
                ? "The group was changed for the selected reports."
                : "The group was changed for the selected report."
            );
            onReportsMoved?.();
          }}
          onClose={() => {
            setOpenMoveDialog(false);
            setReportsToMove([]);
            setOnReportsMoved(undefined);
          }}
        />
      )}
      {openDuplicateDialog && reportToDuplicate && (
        <DuplicateReport
          report={reportToDuplicate}
          duplicateReport={actions.duplicateReport}
          onChanged={() => sendNotification("The report was successfully duplicated.")}
          onClose={() => {
            setOpenDuplicateDialog(false);
            setReportToDuplicate(undefined);
          }}
        />
      )}

      {openShareDialog && reportToShare && (
        <ShareReportWrapper
          report={reportToShare}
          onClose={() => {
            setOpenShareDialog(false);
            setReportToShare(undefined);
          }}
          updateReportAuthorizationInfo={actions.updateReportAuthorizationInfo}
        />
      )}

      {openBulkShareDialog && reportsToShare.length > 0 && (
        <ShareReportsBulkWrapper
          reports={reportsToShare}
          doUpdateSharing={actions.updateReportsSharing}
          onShared={() => {
            sendNotification("The sharing was updated for the selected reports.");
            onReportsShared?.();
          }}
          onClose={() => {
            setOpenBulkShareDialog(false);
            setReportsToShare([]);
            setOnReportsShared(undefined);
          }}
        />
      )}

      {openGenerateApiReport && reportToGenerateApiReport && reportUsage === "Ui" && actions.generateApiReport && (
        <GenerateApiReport
          report={reportToGenerateApiReport}
          generateReport={actions.generateApiReport}
          onChanged={() => sendNotification("The API report was successfully generated.")}
          onClose={() => {
            setOpenGenerateApiReport(false);
            setReportToGenerateApiReport(undefined);
          }}
          openInNewTab={openInNewTab}
        />
      )}
    </ReportsManagerContextProvider>
  );
}
